import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Collapse,
    IconButton,
    LinearProgress,
    Link,
    Paper,
    Tooltip,
    Typography,
} from "@mui/material";
import { ExpandLess, ExpandMore, LocalShipping, Refresh } from "@mui/icons-material";
import { Order } from "../../types";
import { courierService, ParcelTracking, ParcelTrackingStep } from "../../services/courierService";
import { CachedEntry, createLocalCache } from "../../utils/localCache";

/*
 * "Track the parcel" in the order drawer: the Steadfast parcel's steps,
 * fetched only when asked for and kept in this browser, so reopening the
 * order doesn't call Steadfast again. The age of what's shown is always
 * visible, and turns red once it's over an hour old.
 */

const trackingCache = createLocalCache<ParcelTracking>("cps.parcelTracking.v1", { maxEntries: 100 });

/** Shown data older than this is flagged in red. */
const STALE_AFTER_MS = 60 * 60 * 1000;
/** Steps shown before "Show N earlier steps". */
const VISIBLE_STEPS = 4;

/** "Just updated", "12 min old", "2 h 5 min old", "3 d 4 h old". */
export function ageText(ms: number): string {
    const minutes = Math.floor(ms / 60000);
    if (minutes < 1) return "Just updated";
    if (minutes < 60) return `${minutes} min old`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} h${minutes % 60 ? ` ${minutes % 60} min` : ""} old`;
    const days = Math.floor(hours / 24);
    return `${days} d${hours % 24 ? ` ${hours % 24} h` : ""} old`;
}

function stepTime(iso?: string | null): string {
    if (!iso) return "";
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleString("en-GB", {
        day: "numeric",
        month: "short",
        ...(date.getFullYear() !== new Date().getFullYear() ? { year: "numeric" } : {}),
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
    });
}

/** Newest first; steps without a time keep Steadfast's order. */
const newestFirst = (steps: ParcelTrackingStep[]) =>
    steps
        .map((step, index) => ({ step, index, time: step.created_at ? Date.parse(step.created_at) : NaN }))
        .sort((a, b) =>
            Number.isNaN(a.time) || Number.isNaN(b.time) ? b.index - a.index : b.time - a.time,
        )
        .map(({ step }) => step);

/** Re-render every minute while `active`, so "x min old" stays true. */
function useMinuteTick(active: boolean) {
    const [, setTick] = useState(0);
    useEffect(() => {
        if (!active) return;
        const id = setInterval(() => setTick((n) => n + 1), 60000);
        return () => clearInterval(id);
    }, [active]);
}

/**
 * The parcel's tracking for one order: what's cached in this browser, plus
 * `fetchNow` to ask Steadfast (through our API) and refresh the cache.
 * Keyed by order and consignment, so a re-booked parcel starts fresh.
 */
export function useParcelTracking(order: Pick<Order, "order_id" | "consignment_id">) {
    const key = `${order.order_id}:${order.consignment_id}`;
    const [entry, setEntry] = useState<CachedEntry<ParcelTracking> | null>(() => trackingCache.read(key));
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const latest = useRef(0);

    const fetchNow = useCallback(async () => {
        const request = ++latest.current;
        setLoading(true);
        setError("");
        try {
            const data = await courierService.getParcelTracking(order.order_id);
            if (request === latest.current) setEntry(trackingCache.write(key, data));
        } catch (err: any) {
            if (request !== latest.current) return;
            const detail = err?.response?.data?.detail;
            setError(
                typeof detail === "string"
                    ? detail
                    : err?.code === "ECONNABORTED"
                      ? "Steadfast didn't answer in time. Try again."
                      : "Couldn't load the tracking. Try again.",
            );
        } finally {
            if (request === latest.current) setLoading(false);
        }
    }, [key, order.order_id]);

    return { entry, loading, error, fetchNow };
}

function TrackingSteps({ steps }: { steps: ParcelTrackingStep[] }) {
    const [showAll, setShowAll] = useState(false);
    const ordered = useMemo(() => newestFirst(steps), [steps]);
    const visible = showAll ? ordered : ordered.slice(0, VISIBLE_STEPS);
    const hidden = ordered.length - visible.length;

    if (ordered.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                Steadfast hasn't recorded any steps for this parcel yet.
            </Typography>
        );
    }

    return (
        <Box component="ol" sx={{ listStyle: "none", m: 0, p: 0 }}>
            {visible.map((step, i) => {
                const newest = i === 0;
                const last = i === visible.length - 1;
                return (
                    <Box component="li" key={`${step.created_at}-${i}`} sx={{ display: "flex", gap: 1.5 }}>
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", pt: 0.6 }}>
                            <Box
                                sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: "50%",
                                    flexShrink: 0,
                                    bgcolor: newest ? "primary.main" : "grey.400",
                                    boxShadow: newest ? (t) => `0 0 0 3px ${t.palette.primary.main}22` : "none",
                                }}
                            />
                            {!last && <Box sx={{ width: 2, flex: 1, bgcolor: "divider", my: 0.5 }} />}
                        </Box>
                        <Box sx={{ pb: last ? 0 : 1.5, minWidth: 0 }}>
                            <Typography
                                variant="body2"
                                sx={{ fontWeight: newest ? 600 : 400, wordBreak: "break-word" }}
                            >
                                {step.text || "Update"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {stepTime(step.created_at)}
                            </Typography>
                        </Box>
                    </Box>
                );
            })}
            {(hidden > 0 || showAll) && ordered.length > VISIBLE_STEPS && (
                <Box component="li" sx={{ pl: 3.25, pt: 0.5 }}>
                    <Link component="button" type="button" variant="caption" onClick={() => setShowAll((v) => !v)}>
                        {showAll ? "Show fewer steps" : `Show ${hidden} earlier step${hidden === 1 ? "" : "s"}`}
                    </Link>
                </Box>
            )}
        </Box>
    );
}

/** Mount with `key={order.order_id}` so switching orders starts collapsed. */
export const ParcelTrackingPanel: React.FC<{ order: Order }> = ({ order }) => {
    const { entry, loading, error, fetchNow } = useParcelTracking(order);
    const [expanded, setExpanded] = useState(false);
    useMinuteTick(!!entry);

    const age = entry ? Date.now() - entry.savedAt : 0;
    const stale = !!entry && age > STALE_AFTER_MS;

    const toggle = () => {
        if (expanded) {
            setExpanded(false);
            return;
        }
        setExpanded(true);
        if (!entry && !loading) void fetchNow();
    };

    const refresh = () => {
        setExpanded(true);
        void fetchNow();
    };

    return (
        <Paper variant="outlined" sx={{ mb: 3, overflow: "hidden" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1, py: 0.5, flexWrap: "wrap" }}>
                <Button
                    size="small"
                    startIcon={<LocalShipping fontSize="small" />}
                    endIcon={expanded ? <ExpandLess /> : <ExpandMore />}
                    onClick={toggle}
                    aria-expanded={expanded}
                >
                    Track the parcel
                </Button>
                <Box sx={{ flex: 1 }} />
                {entry && (
                    <>
                        <Typography
                            variant="caption"
                            color={stale ? "error" : "text.secondary"}
                            sx={{ fontWeight: stale ? 600 : 400 }}
                        >
                            {ageText(age)}
                        </Typography>
                        <Tooltip title="Refresh from Steadfast">
                            <span>
                                <IconButton
                                    size="small"
                                    color={stale ? "error" : "default"}
                                    aria-label="Refresh parcel tracking"
                                    onClick={refresh}
                                    disabled={loading}
                                >
                                    <Refresh fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </>
                )}
            </Box>
            {loading && <LinearProgress sx={{ height: 2 }} />}
            <Collapse in={expanded && (!!entry || !!error)} unmountOnExit>
                <Box sx={{ px: 2, pt: 1.5, pb: 2, borderTop: 1, borderColor: "divider" }}>
                    {error && (
                        <Alert
                            severity="warning"
                            sx={{ mb: entry ? 1.5 : 0, py: 0 }}
                            action={
                                <Button color="inherit" size="small" onClick={() => void fetchNow()} disabled={loading}>
                                    Retry
                                </Button>
                            }
                        >
                            {error}
                        </Alert>
                    )}
                    {entry && <TrackingSteps steps={entry.data.steps} />}
                </Box>
            </Collapse>
        </Paper>
    );
};
